// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Avatar, Button, ConfirmDialog, useToast } from "@chotca/ui";
import { Eye, UserPlus, X as XIcon } from "lucide-react";
import { getSdk } from "@chotca/api-client";
import { qk } from "@chotca/api-client";
import { WorkerProfileModal } from "@/components/worker-profile-modal";
import { InviteApplicantDialog } from "@/components/employer/invite-applicant-dialog";

type Applicant = {
  application: {
    id: string;
    state: string;
    appliedAt: string;
    note?: string | null;
  };
  worker: {
    userId: string;
    fullName: string;
    avatarUrl?: string | null;
    currentDistrict?: string | null;
    kycVerifiedAt?: string | null;
    ratingAvg: number | null;
    ratingCount: number;
  };
  invited: boolean;
  committedAt: string | null;
  contact: { phone: string | null; email: string | null } | null;
};

export function ApplicantRow({
  applicant,
  postingId,
  orgId,
  postingActive = true,
}: {
  applicant: Applicant;
  postingId: string;
  orgId: string;
  /**
   * False when the posting is CLOSED / EXPIRED / CANCELLED. Hides invite &
   * reject actions and locks profile-modal browsing for not-yet-invited
   * applicants. Already-invited contacts stay visible regardless.
   */
  postingActive?: boolean;
}) {
  // `invited` = an invite has been sent. `contact` only arrives once the
  // invite commits (worker's 30-min window elapsed), so it can't stand in for
  // "invited" anymore — a freshly-invited applicant has invited=true but no
  // contact yet.
  const invited = applicant.invited;
  const canBrowseProfile = postingActive || invited;
  const canAct = postingActive && !invited;
  const qc = useQueryClient();
  const t = useTranslations("postings");
  const { toast } = useToast();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [confirmKind, setConfirmKind] = React.useState<"reject" | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: qk.applications.byPosting(postingId) });
    qc.invalidateQueries({
      queryKey: qk.applications.profile(applicant.application.id),
    });
    // Dashboard "Cần xử lý" feed listens on this key — invalidate so the
    // row disappears from the dashboard the moment we invite/reject here.
    qc.invalidateQueries({ queryKey: qk.applications.pending });
  }

  const reject = useMutation({
    mutationFn: () => getSdk().applications.reject(applicant.application.id),
    onSuccess: () => {
      invalidate();
      toast({ tone: "success", title: t("rejectSuccess") });
      setConfirmKind(null);
      setProfileOpen(false);
    },
    onError: () => {
      toast({ tone: "error", title: t("rejectError") });
    },
  });

  return (
    <>
      <div className="rounded-lg border border-ink/10 bg-paper p-4">
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            onClick={() => canBrowseProfile && setProfileOpen(true)}
            disabled={!canBrowseProfile}
            className="flex flex-1 items-start gap-3 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-100"
          >
            <Avatar
              name={applicant.worker.fullName}
              src={applicant.worker.avatarUrl ?? undefined}
              size="lg"
              tone="lime"
            />
            <div className="min-w-0">
              <p className="wrap-break-word text-sm font-semibold text-ink">
                {applicant.worker.fullName}{" "}
                {applicant.worker.kycVerifiedAt ? (
                  <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                    ✓ CCCD
                  </span>
                ) : null}
              </p>
              <p className="text-xs text-ink/60">
                {applicant.worker.currentDistrict ?? "—"} ·{" "}
                {applicant.worker.ratingAvg !== null
                  ? `⭐ ${applicant.worker.ratingAvg.toFixed(1)} (${applicant.worker.ratingCount})`
                  : "Chưa có đánh giá"}
              </p>
              {applicant.application.note ? (
                <p className="mt-1 text-xs italic text-ink/70">
                  “{applicant.application.note}”
                </p>
              ) : null}
            </div>
          </button>

          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            {applicant.contact ? (
              <div className="min-w-0 text-left text-xs sm:text-right">
                <p className="break-all font-mono text-ink">
                  {applicant.contact.phone ?? "Chưa có SĐT"}
                </p>
                {applicant.contact.email ? (
                  <p className="break-all text-ink/60">
                    {applicant.contact.email}
                  </p>
                ) : null}
                {applicant.contact.phone ? (
                  <a
                    href={`https://zalo.me/${applicant.contact.phone.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block rounded-full bg-emerald-600 px-3 py-1 text-[11px] font-bold text-white"
                  >
                    Mở Zalo
                  </a>
                ) : null}
              </div>
            ) : invited ? (
              <div className="text-left text-xs sm:text-right">
                <p className="font-semibold text-ink">Đã mời phỏng vấn</p>
                <p className="text-ink/60">
                  Chờ ứng viên xác nhận. SĐT hiện sau khi chốt lịch.
                </p>
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setProfileOpen(true)}
                  aria-label={t("viewProfile")}
                  title={t("viewProfile")}
                  className="mt-1 transition-colors hover:bg-mist"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ) : canAct ? (
              <div className="flex flex-wrap gap-2 sm:justify-end">
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setProfileOpen(true)}
                  aria-label={t("viewProfile")}
                  title={t("viewProfile")}
                  className="transition-colors hover:bg-mist"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => setInviteOpen(true)}
                  disabled={reject.isPending}
                  className="transition-transform hover:-translate-y-0.5"
                >
                  <UserPlus className="h-4 w-4" />
                  Mời PV
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmKind("reject")}
                  loading={reject.isPending}
                  className="transition-transform hover:-translate-y-0.5"
                >
                  <XIcon className="h-4 w-4" />
                  {t("reject")}
                </Button>
              </div>
            ) : !postingActive ? (
              <p className="text-xs text-ash">Tin đã đóng</p>
            ) : null}
          </div>
        </div>
      </div>

      <WorkerProfileModal
        applicationId={applicant.application.id}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onInvite={() => {
          setProfileOpen(false);
          setInviteOpen(true);
        }}
        onReject={() => setConfirmKind("reject")}
        invitePending={false}
        rejectPending={reject.isPending}
        inviteLabel="Mời phỏng vấn"
      />

      <InviteApplicantDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={invalidate}
        orgId={orgId}
        applicationId={applicant.application.id}
        workerName={applicant.worker.fullName}
      />

      <ConfirmDialog
        open={confirmKind === "reject"}
        onClose={() => setConfirmKind(null)}
        onConfirm={() => reject.mutate()}
        title={t("rejectConfirmTitle")}
        description={t("rejectConfirmDesc")}
        confirmLabel={t("rejectConfirmCta")}
        destructive
        loading={reject.isPending}
      />
    </>
  );
}
